import { Component, OnInit } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
  SlotClassArgs,
  DateChangeEvent,
  EventStyleArgs,
  Group,
} from '@progress/kendo-angular-scheduler';
import { Day } from '@progress/kendo-date-math';
import { map, shareReplay, startWith, switchMap } from 'rxjs';
import { IKendoSchedulerResource, ISchedulerEvent } from 'src/app/scheduler/models/scheduler';
import { IHoliday } from 'src/app/setting/holiday/model/holiday';
import { RessourceType } from 'src/app/setting/models/ressource';
import { TenantSettingsStoreService } from 'src/app/setting/tenant-settings/services/tenant-settings-store.service';
import { AbsenceState } from 'src/app/time-tracking/models/absence-group';
import { AbsenceGroupStoreService } from 'src/app/time-tracking/storage/absence-group-store.service';
import { WorkTimeSchedule, WorkTimeScheduleService } from '../services/work-time-schedule.service';
import { HolidayService } from 'src/app/setting/holiday/services/holiday.service';
import { PermissionStorageService } from 'src/app/authentication/storages/permission-storage.service';
import { IBaseSearchRequest } from 'src/app/core/models/base';
import { findHoliday } from '../helpers/holiday';


@UntilDestroy()
@Component({
    selector: 'app-absence-scheduler',
    templateUrl: './absence-scheduler.component.html',
    styleUrls: ['./absence-scheduler.component.css'],
    standalone: false
})
export class AbsenceSchedulerComponent implements OnInit {
  constructor(
    private absenceGroupStore : AbsenceGroupStoreService,
    private tenantSettings: TenantSettingsStoreService,
    private workTimeSchedules: WorkTimeScheduleService,
    private holidayService: HolidayService,
    private permissionStore: PermissionStorageService
  ) {}

  tenantSettings$ = this.tenantSettings.tenantSettings$;

  holidays: IHoliday[] = [];

  // Same range filter as the main planner and the dashboard; values are set on every date change.
  private listHolidayRequest: IBaseSearchRequest = {
    pageNumber: 0,
    pageSize: 999999,
    advancedFilter: {
      logic: 'and',
      filters: [
        { field: 'start', operator: 'gte', value: null },
        { field: 'end', operator: 'lte', value: null },
      ],
    },
  };

  events$ = this.absenceGroupStore.absenceEvents$;
  
  /** Worktime schedules keyed by employee id (the resource id in this scheduler). */
  private workTimesByEmployee = new Map<string, WorkTimeSchedule[]>();

  // Same pensum visualisation as the main planner (scheduler-shared): once the resources are
  // known, load the employees' worktimes and re-emit the resources as a new array so the
  // scheduler re-renders its slots through the slotClass callback.
  resources$ = this.absenceGroupStore.resources$.pipe(
    switchMap((resources) => {
      const employeeIds = (resources?.[0]?.data ?? []).map((resource) => resource.id);
      return this.workTimeSchedules.loadSchedules(employeeIds).pipe(
        map((schedules) => {
          this.workTimesByEmployee = schedules;
          return [...resources];
        }),
        startWith(resources)
      );
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  group: Group = {
    resources: ['Ressources'],
    orientation: 'vertical',
  };

  public weekStart: Day = Day.Monday;

  public selectedDate: Date = new Date();

  get RessourceTypes(){
    return RessourceType
  }

  ngOnInit(): void {
    this.absenceGroupStore.initRessources();
  }

  setSlotBackgroundColor = (args: SlotClassArgs) => {
    const classes: string[] = [];

    if (findHoliday(this.holidays, args.start)) {
      classes.push('holiday');
    }

    const employeeId = args.resources?.[0]?.id;
    if (
      employeeId &&
      this.workTimeSchedules.isNonWorkingSlot(this.workTimesByEmployee.get(employeeId), args)
    ) {
      classes.push('non-working-time');
    }

    return classes;
  };

  // Shown under the date in the date header so users see why a day is red.
  holidayName = (date: Date): string | undefined => findHoliday(this.holidays, date)?.name;

  onDateChange(event: DateChangeEvent): void {
    this.absenceGroupStore.loadScheudler(event);
    this.loadHolidays(event.dateRange.start, event.dateRange.end);
  }

  private loadHolidays(start: Date, end: Date): void {
    const permissions = this.permissionStore.getPermissions();
    if (!permissions?.includes('Permissions.Holidays.Search')) {
      return;
    }

    this.listHolidayRequest.advancedFilter.filters[0].value = start;
    this.listHolidayRequest.advancedFilter.filters[1].value = end;

    this.holidayService.searchHolidays(this.listHolidayRequest)
      .pipe(untilDestroyed(this))
      .subscribe(response => this.holidays = response.data);
  }

  setEventClass = (args: EventStyleArgs) => {
    const event: ISchedulerEvent = args.event;
    const status = event.dataItem.absenceState;

    if (status === AbsenceState.Open) {
      return "open";
    }

    if (status === AbsenceState.Accepted) {
      return "accepted";
    }

    if (status === AbsenceState.Declined) {
      return "declined";
    }
  };
}
